const REBUY_BUNDLE_WIDGET_SELECTOR = '#rebuy-widget-286729';
const REBUY_SELECTED_OPTION_SELECTOR =
  '.rebuy-bundle-builder__action-box-cta:has(.rebuy-radio:checked)';
const REBUY_STEP_TABS_SELECTOR = '.rebuy-bundle-builder__step-tabs';
const REBUY_TAB_WRAPPER_SELECTOR = '.rebuy-bundle-builder__step-tabs-wrapper';
const RTNU_BUNDLE_SUBTOTAL_CLASS = 'rtnu-bundle-subtotal';
const RTNU_SCROLL_FORWARD_CLASS = 'rtnu-bundle-scroll-forward';

/**
 * @param {Element | { querySelector: Function } | null} widget
 * @returns {string}
 */
const getSelectedSubtotalText = (widget) => {
  const selectedOption = widget?.querySelector(REBUY_SELECTED_OPTION_SELECTOR);
  const subtotal = selectedOption?.querySelector('.rebuy-money.sale');
  const visibleSubtotal = selectedOption?.querySelector(
    '.rebuy-money.sale > :not(.sr-only)',
  );

  return visibleSubtotal?.textContent?.trim() || subtotal?.textContent?.trim() || '';
};

/**
 * @param {{ scrollLeft: number, clientWidth: number, scrollWidth: number }} scroller
 * @returns {{ canScrollBackward: boolean, canScrollForward: boolean }}
 */
const getHorizontalScrollState = ({ scrollLeft, clientWidth, scrollWidth }) => ({
  canScrollBackward: scrollLeft > 1,
  canScrollForward: scrollLeft + clientWidth < scrollWidth - 1,
});

/**
 * @param {{ scrollLeft: number, clientWidth: number, scrollWidth: number }} scroller
 * @param {ArrayLike<{ offsetLeft: number }>} tabs
 * @returns {number}
 */
const getNextTabScrollLeft = ({ scrollLeft, clientWidth, scrollWidth }, tabs) => {
  const maximumScrollLeft = Math.max(0, scrollWidth - clientWidth);
  const tabItems = Array.from(tabs);
  const firstTabOffset = tabItems[0]?.offsetLeft ?? 0;
  const nextTab = tabItems.find(
    (tab) => tab.offsetLeft - firstTabOffset > scrollLeft + 1,
  );
  const nextTabScrollLeft = nextTab
    ? nextTab.offsetLeft - firstTabOffset
    : maximumScrollLeft;

  return Math.min(nextTabScrollLeft, maximumScrollLeft);
};

/**
 * @param {string} text
 * @returns {string}
 */
const preserveParentheticalPhrases = (text) =>
  text.replace(/\([^)]*\)/g, (phrase) => phrase.replace(/\s+/g, '\u00a0'));

/**
 * @param {HTMLElement} widget
 * @returns {void}
 */
const syncCollapsedSubtotal = (widget) => {
  const header = widget.querySelector('.rebuy-bundle-builder__group-container-header');
  if (!header) return;

  let subtotal = header.querySelector(`.${RTNU_BUNDLE_SUBTOTAL_CLASS}`);

  if (!subtotal) {
    subtotal = document.createElement('span');
    subtotal.className = RTNU_BUNDLE_SUBTOTAL_CLASS;
    subtotal.setAttribute('aria-live', 'polite');

    const expandButton = header.querySelector(
      '.rebuy-bundle-builder__group-container-header-expand',
    );
    header.insertBefore(subtotal, expandButton);
  }

  const subtotalText = getSelectedSubtotalText(widget);
  const nextText = subtotalText ? `Subtotal ${subtotalText}` : '';

  if (subtotal.textContent !== nextText) {
    subtotal.textContent = nextText;
  }

  subtotal.hidden = !subtotalText;
};

/**
 * @param {HTMLElement} widget
 * @param {() => void} scheduleSync
 * @param {AbortSignal} signal
 * @returns {void}
 */
const syncTabScroller = (widget, scheduleSync, signal) => {
  const scroller = widget.querySelector(REBUY_TAB_WRAPPER_SELECTOR);
  if (!scroller) return;

  const tabTitles = scroller.querySelectorAll(
    '.rebuy-bundle-builder__step-tab-title',
  );

  for (const title of tabTitles) {
    const nextText = preserveParentheticalPhrases(title.textContent || '');

    if (title.textContent !== nextText) {
      title.textContent = nextText;
    }
  }

  const { canScrollBackward, canScrollForward } = getHorizontalScrollState(scroller);
  scroller.classList.toggle('rtnu-can-scroll-backward', canScrollBackward);
  scroller.classList.toggle('rtnu-can-scroll-forward', canScrollForward);

  const tabContainer = widget.querySelector(REBUY_STEP_TABS_SELECTOR);
  let forwardButton = tabContainer?.querySelector(`.${RTNU_SCROLL_FORWARD_CLASS}`);

  if (tabContainer && !forwardButton) {
    forwardButton = document.createElement('button');
    forwardButton.type = 'button';
    forwardButton.className = RTNU_SCROLL_FORWARD_CLASS;
    forwardButton.setAttribute('aria-label', 'Show next bundle category');

    if (!scroller.id) {
      scroller.id = 'RebuyBundleTabs-286729';
    }

    forwardButton.setAttribute('aria-controls', scroller.id);
    tabContainer.append(forwardButton);
  }

  if (forwardButton) {
    const shouldHideForwardButton = !canScrollForward;

    if (forwardButton.hidden !== shouldHideForwardButton) {
      forwardButton.hidden = shouldHideForwardButton;
    }
  }

  if (scroller.dataset.rtnuScrollListener === 'true') return;

  scroller.dataset.rtnuScrollListener = 'true';
  scroller.addEventListener('scroll', scheduleSync, { passive: true, signal });
};

/**
 * Initialize page-scoped presentation enhancements for the Rebuy bundle builder.
 *
 * @returns {void}
 */
const initializeRebuyBundleEnhancements = () => {
  const abortController = new AbortController();
  const { signal } = abortController;
  let animationFrameId = 0;
  let observedWidget = null;
  let widgetObserver = null;

  const syncWidget = () => {
    animationFrameId = 0;

    const widget = document.querySelector(REBUY_BUNDLE_WIDGET_SELECTOR);
    if (!widget) return;

    if (widget !== observedWidget) {
      widgetObserver?.disconnect();
      observedWidget = widget;
      widgetObserver = new MutationObserver(scheduleSync);
      widgetObserver.observe(widget, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
        attributeFilter: ['class', 'checked'],
      });
    }

    syncCollapsedSubtotal(widget);
    syncTabScroller(widget, scheduleSync, signal);
  };

  function scheduleSync() {
    if (animationFrameId) return;
    animationFrameId = requestAnimationFrame(syncWidget);
  }

  const handleWidgetInteraction = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(REBUY_BUNDLE_WIDGET_SELECTOR)) return;

    const forwardButton = event.target.closest(`.${RTNU_SCROLL_FORWARD_CLASS}`);

    if (forwardButton) {
      const widget = forwardButton.closest(REBUY_BUNDLE_WIDGET_SELECTOR);
      const scroller = widget?.querySelector(REBUY_TAB_WRAPPER_SELECTOR);

      if (scroller) {
        const tabs = scroller.querySelectorAll('.rebuy-bundle-builder__step-tab');
        const nextScrollLeft = getNextTabScrollLeft(scroller, tabs);
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth';

        scroller.scrollTo({ left: nextScrollLeft, behavior });
      }
    }

    scheduleSync();
  };

  for (const eventName of [
    'rebuy.ready',
    'rebuy.addProductToBundle',
    'rebuy.removeProductFromBundle',
  ]) {
    document.addEventListener(eventName, scheduleSync, { signal });
  }

  document.addEventListener('change', handleWidgetInteraction, { signal });
  document.addEventListener('click', handleWidgetInteraction, { signal });
  window.addEventListener('resize', scheduleSync, { signal });
  window.addEventListener(
    'pagehide',
    () => {
      abortController.abort();
      widgetObserver?.disconnect();
      cancelAnimationFrame(animationFrameId);
    },
    { once: true },
  );

  scheduleSync();
};

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    getHorizontalScrollState,
    getNextTabScrollLeft,
    getSelectedSubtotalText,
    preserveParentheticalPhrases,
  };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  initializeRebuyBundleEnhancements();
}
